import { useEffect } from 'react';
import { connect, useDispatch } from 'react-redux';
import type { Dispatch } from 'redux';
import { Form } from 'react-final-form';

import { Button } from 'UIPackage/MainModule/components/Button';
import { TextInputField } from 'UIPackage/MainModule/components/TextInputField';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { AdminSettingCodesEnum } from 'APIPackage/AdminModule/enums/AdminSettingCodesEnum';
import {
  adminGetFetchSettings,
  setImmediateTestPurchase,
} from 'PersonalAccountPackage/AdminModule/actions/adminAction';
import { getAdminFetchSettings } from 'PersonalAccountPackage/AdminModule/selectors/adminSelectors';

import { FormValues, IImmediateTestPurchaseFormStoreProps } from '../interfaces/ImmediateTestPurchaseFormInterfaces';
import styles from './ImmediateTestPurchaseForm.module.scss';

interface IImmediateTestPurchaseFormContentProps {
  onSubmit: (values: FormValues) => void;
  initialValues: FormValues;
}

const ImmediateTestPurchaseFormContent = ({ onSubmit, initialValues }: IImmediateTestPurchaseFormContentProps) => {
  const validateDays = (value: string): string | undefined => {
    if (value === '') {
      return undefined;
    }

    const numValue = Number(value);

    if (Number.isNaN(numValue) || !Number.isInteger(numValue)) {
      return 'Введите целое число';
    }

    if (numValue < 0 || numValue > 365) {
      return 'Введите число от 0 до 365';
    }

    return undefined;
  };

  const formatInput = (value: string): string => {
    if (value === null || value === undefined) return '';

    return value.toString().replace(/[^\d]/g, '');
  };

  const parseInput = (value: string): string => {
    if (value === null || value === undefined) return '';

    return value.toString().replace(/[^\d]/g, '');
  };

  return (
    <Form<FormValues>
      onSubmit={onSubmit}
      initialValues={initialValues}
      enableReinitialize={true}
      subscription={{ submitting: true, pristine: true, valid: true, errors: true, values: true }}
    >
      {({ handleSubmit, submitting, pristine, errors, values }) => {
        const isSubmitDisabled = submitting || pristine || Boolean(errors?.days) || !values.days;

        return (
          <form onSubmit={handleSubmit} className={styles.formContainer}>
            <TextInputField
              name="days"
              className={styles.formContainerInput}
              placeholder="Введите число от 0 до 365"
              inputMode="numeric"
              format={formatInput}
              parse={parseInput}
              validate={validateDays}
              helpText="Допустимый диапазон: от 0 до 365 дней"
            />
            <Button
              className={styles.saveButton}
              type="submit"
              color="primary"
              size="medium"
              text="Сохранить"
              disabled={isSubmitDisabled}
            />
          </form>
        );
      }}
    </Form>
  );
};

const ImmediateTestPurchaseFormWithoutStore = ({ settings }: IImmediateTestPurchaseFormStoreProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();

  useEffect(() => {
    dispatch(adminGetFetchSettings());
  }, [dispatch]);

  const onSubmit = (values: FormValues) => {
    const daysValue = values.days === '' ? 0 : Number(values.days);

    dispatch(setImmediateTestPurchase(daysValue, settings));
  };

  const getInitialValue = (): FormValues => {
    const immediateTestPurchaseSetting = settings.find(
      setting => setting.code === AdminSettingCodesEnum.IMMEDIATE_TEST_PURCHASE_DAYS,
    );

    return {
      days: immediateTestPurchaseSetting?.value || '',
    };
  };

  return (
    <div className={styles.container}>
      <h2>Настройка незамедлительной контрольной закупки</h2>
      <p>
        Количество рабочих дней доступных в календаре, в пределах которых, отсчитывая от текущей даты в прошлое, можно
        создавать внеплановый паспорт КНМ с типом основания «Незамедлительная контрольная закупка».
      </p>
      <ImmediateTestPurchaseFormContent onSubmit={onSubmit} initialValues={getInitialValue()} />
    </div>
  );
};

const mapStateToProps = (state: IRootState): IImmediateTestPurchaseFormStoreProps => ({
  settings: getAdminFetchSettings(state),
});

export const ImmediateTestPurchaseForm = connect(mapStateToProps)(ImmediateTestPurchaseFormWithoutStore);
